/**
 * lc-klaviyo.js, Klaviyo onsite event tracking for Lost Collective Dawn.
 *
 * Events fired:
 *   Viewed Product     on product page load, from window.lcKlaviyoProduct
 *   Added to Cart      on lc:cart-add custom event (dispatched by main-product.liquid)
 *   Checkout Started   on click of any checkout link or button
 *
 * The Klaviyo base JS (klaviyo.js?company_id=...) is loaded async from
 * layout/theme.liquid. This file runs deferred so the queue is always
 * ready, but the Sprint 9 conversion audit found 0 Viewed Product events
 * in the last 90 days despite 1,667 view_item users in GA4. Root cause
 * was the script pushing to window.klaviyo: by the time the deferred
 * script ran, the SDK had already replaced window.klaviyo with its
 * object form, and array-style push silently threw. Switched to the
 * legacy _learnq queue, which the onsite klaviyo.js loader documents
 * and drains in order regardless of script-load timing.
 */

(function () {
  var lq = (window._learnq = window._learnq || []);

  // Viewed Product — data injected by layout/theme.liquid on product template pages.
  if (window.lcKlaviyoProduct) {
    var p = window.lcKlaviyoProduct;
    lq.push(['track', 'Viewed Product', p]);
    lq.push([
      'trackViewedItem',
      {
        Title: p.ProductName,
        ItemId: p.ProductID,
        Categories: p.Categories,
        ImageUrl: p.ImageURL,
        Url: p.URL,
        Metadata: {
          Brand: p.Brand,
          Price: p.Price,
          CompareAtPrice: p.CompareAtPrice,
        },
      },
    ]);
  }

  // Added to Cart — dispatched by main-product.liquid after a successful
  // /cart/add.js fetch. e.detail is the first item from the Shopify cart response.
  document.addEventListener('lc:cart-add', function (e) {
    var item = e.detail;
    if (!item) return;
    lq.push([
      'track',
      'Added to Cart',
      {
        $value: (item.price || 0) / 100,
        AddedItemProductName: item.product_title || '',
        AddedItemProductID: item.product_id || '',
        AddedItemSKU: item.sku || '',
        // item.image has three shapes: empty, fully-qualified (https://...), protocol-relative (//...)
        AddedItemImageURL: (function (img) {
          if (!img) return '';
          if (img.indexOf('http') === 0) return img;
          if (img.indexOf('//') === 0) return 'https:' + img;
          return img;
        })(item.image),
        AddedItemURL: window.location.origin + (item.url || ''),
        AddedItemPrice: (item.price || 0) / 100,
        AddedItemQuantity: item.quantity || 1,
        CheckoutURL: window.location.origin + '/checkout',
      },
    ]);
  });

  // Checkout Started — fires on any click that leads to checkout, cart page
  // button or cart drawer link. Event delegation on document catches
  // dynamically rendered drawer content.
  document.addEventListener('click', function (e) {
    var el = e.target.closest('a[href*="/checkout"], button[name="checkout"]');
    if (!el) return;
    lq.push(['track', 'Checkout Started', {}]);
  });
})();

// Series interest capture — sets featured_series_* profile properties when a
// Klaviyo hosted form is submitted. Derives the series from the current page
// (primary) or the most-visited series in the current browser session
// (fallback for non-series pages like the homepage).
//
// Primary:  product page carries the series via lcKlaviyoProduct.Categories[0];
//           collection page carries it in the URL handle.
// Fallback: each series page visit is recorded to sessionStorage; on form
//           submit from a non-series page, the highest-count series wins.
//
// The confidence value 'browse-context' passes the W-N-2 dynamic block gate
// (anything except "none", "R3", or "").
(function () {
  var LC_SERIES = {
    'white-bay-power-station': {
      name: 'White Bay Power Station',
      ql: 'White Bay generated electricity for Sydney\'s tram and rail network from 1917. At its 1923 peak, that network was the largest in the Southern Hemisphere.',
      qb: 'Generation ceased Christmas Eve 1983, after 70 years of service. It stands intact on the western shore of White Bay.'
    },
    'wangi-power-station': {
      name: 'Wangi Power Station',
      ql: 'When the NSW grid went dark on 10 June 1964, it was Wangi\'s boilers — kept alight through the shutdown — that helped bring the state back online.',
      qb: '330 MW under one 228-metre roof. Approximately 3 million bricks. Decommissioned 1989.'
    },
    'callan-park': {
      name: 'Callan Park',
      ql: 'Australia\'s first purpose-built moral therapy hospital — a design doctrine holding that light, landscape and a distributed pavilion layout were the treatment.',
      qb: 'Built 1880 to 1884 at a cost of £235,539. By 1930 it held 1,500 patients against a designed capacity of around 600. Closed 30 April 2008.'
    },
    'kenmore-asylum': {
      name: 'Kenmore Asylum',
      ql: 'Kenmore opened in 1895 as the first purpose-built complex for mental health care in rural NSW, with capacity for 700 patients across 19 wards.',
      qb: 'Designed by Walter Liberty Vernon, NSW Government Architect. Photographed 1 March 2020 — before the 2021 fire destroyed Ward 15.'
    },
    'waterfall-sanatorium': {
      name: 'Waterfall Sanatorium',
      ql: 'Waterfall Sanatorium received its first patients on 14 April 1909 and by 1919 held 788 — the largest sanatorium in NSW.',
      qb: 'Site chosen at about 1,000 feet elevation: medical orthodoxy held that tuberculosis patients needed rarefied air. Approximately 2,000 graves remain in the cemetery.'
    },
    'kinugawa-kan': {
      name: 'Kinugawa Kan',
      ql: 'Kinugawa Kan was the first hotel at Kinugawa Onsen to fail after the bubble, filing for bankruptcy in June 1999 with approximately 3 billion yen in debts.',
      qb: 'Nine storeys. 70 guest rooms. One named hot-spring bath. Demolishing it is estimated at around 600 million yen — it sits above active hot springs on the gorge cliff.'
    },
    'kandos-cement-works': {
      name: 'Kandos Cement Works',
      ql: 'Kandos was the sole cement supplier to the Sydney Harbour Bridge — confirmed in Lawrence Ennis\'s 1932 booklet listing the Bridge\'s suppliers by name.',
      qb: 'Production began August 1916 and ran for 95 years until September 2011. The town of Kandos exists because the plant was built.'
    },
    'hotel-motel-101': {
      name: 'Hotel Motel 101',
      ql: '101 roadside motels photographed across NSW in 2018 — three runs out of Sydney, shot late at night or very early in the morning, with no people in frame.',
      qb: 'The Thirroul Beach Motel appears in the series. Room 4. Brett learned only after publishing that room 4 is where Brett Whiteley died on 15 June 1992.'
    },
    'eveleigh-paint-shop': {
      name: 'Eveleigh Paint Shop',
      ql: 'Graded Exceptional Significance on the NSW State Heritage Register — the highest grade in the North Eveleigh precinct.',
      qb: 'The 1887 brick wing finished its last carriage in 1989, ending integrated state-run heavy rail manufacturing in inner Sydney after more than a century.'
    },
    'bankstown-rsl': {
      name: 'Bankstown RSL',
      ql: 'The City of Bankstown RSL sub-Branch was founded on 17 September 1928 by 26 returned servicemen. The 1955 clubhouse stood 64 years before demolition in March 2019.',
      qb: 'Below street level: a pool, a squash court, a gym and a sauna. The pool was drained and the squash court floor had begun to peel by the time the photographs were made.'
    },
    'bradmill-denim': {
      name: 'Bradmill Denim',
      ql: 'At its peak Bradmill was the largest textile manufacturer in Australia, processing 40,000 bales of cotton a year at Yarraville — the third-largest water user in Victoria.',
      qb: 'Bradmill began producing denim in 1945, becoming Australia\'s only manufacturer of indigo denim. When cheap imports closed the plant, Australia stopped making it domestically.'
    },
    'blayney-abattoir': {
      name: 'Blayney Abattoir',
      ql: 'At its peak the Blayney Abattoir employed approximately 1,600 people. In the weeks before it closed in March 1998, around 100 remained.',
      qb: 'In its final weeks the sheep chain processed 3,000 to 4,000 animals a day. The site has stood disused since closure.'
    },
    'terminus-hotel': {
      name: 'Terminus Hotel',
      ql: 'A licensed pub has stood on the corner of Harris and John Streets, Pyrmont, almost continuously since 1841, under four different names.',
      qb: 'The 1917 Tooth and Co. rebuild closed in 1983 as Pyrmont\'s industrial base collapsed. It stood empty for 33 years before reopening in 2018.'
    },
    'tin-city': {
      name: 'Tin City',
      ql: 'Eleven shacks remain at Tin City, on the Stockton Bight dunes in Port Stephens, on Worimi country — down from a peak of around 36 to 38 during the 1930s Depression.',
      qb: 'The dunes are the largest mobile coastal sand mass in the Southern Hemisphere, migrating approximately 4 metres northward each year.'
    },
    'ansto-hifar': {
      name: 'ANSTO HIFAR',
      ql: 'Australia\'s first nuclear reactor ran for 49 years and 4 days, from 26 January 1958 to 30 January 2007.',
      qb: 'Built for a civil nuclear power programme Australia never built. What kept it running was isotope production for nuclear medicine and silicon irradiation for semiconductors.'
    },
    'ashio-copper-mine': {
      name: 'Ashio Copper Mine',
      ql: 'When the Tsudo Ore-Dressing Plant closed in 1973, the ore in the hoppers stayed where it was — piled around a spade in the position its last operator left it.',
      qb: 'The plant ran for 51 years on a site whose pollution destroyed the Watarase River basin. PVC pipes still carried water through it under gravity in 2016.'
    },
    'shimizusawa-thermal-power-plant': {
      name: 'Shimizusawa Thermal Power Plant',
      ql: 'The Shimizusawa plant opened in March 1926 and reached 74,500 kW installed output by 1941, powering Hokutan\'s coal-mining network across more than 100 kilometres.',
      qb: 'It burned four resources: coal, mine-induced gas, coke-oven gas from 1954, kerosene from 1971 — described in Japan Heritage materials as a unique four-resource generating site.'
    },
    'shimizusawa-thermal-power-station': {
      name: 'Shimizusawa Thermal Power Plant',
      ql: 'The Shimizusawa plant opened in March 1926 and reached 74,500 kW installed output by 1941, powering Hokutan\'s coal-mining network across more than 100 kilometres.',
      qb: 'It burned four resources: coal, mine-induced gas, coke-oven gas from 1954, kerosene from 1971 — described in Japan Heritage materials as a unique four-resource generating site.'
    },
    'kuwashima-hospital': {
      name: 'Kuwashima Hospital',
      ql: 'Kuwashima Hospital stands in Yubari, Hokkaido — a city whose population collapsed from over 100,000 in the 1960s to fewer than 7,000 today as the coal mines closed.',
      qb: 'The rehabilitation ward still holds orthopaedic and physiotherapy equipment — a building that treated bodies, in a city that ran out of people to treat.'
    },
    'woolla': {
      name: 'Woolla',
      ql: 'The Davis family took up freehold title to Woolla in 1910 and held it continuously until 2004, when Vern — the last family member — died in Braidwood Hospital.',
      qb: 'Two hand-split slab huts on a bend of the Deua River, south of Braidwood. Vern kept calendars on the walls — dates in the photographs run from 1972 through 1991.'
    }
  };

  // Reverse index: series name -> data (for product-page lookup via Categories).
  var LC_SERIES_BY_NAME = {};
  // Reverse index: series name -> collection handle (for featured_series_handle property).
  var LC_SERIES_HANDLE = {};
  Object.keys(LC_SERIES).forEach(function (h) {
    LC_SERIES_BY_NAME[LC_SERIES[h].name] = LC_SERIES[h];
    LC_SERIES_HANDLE[LC_SERIES[h].name] = h;
  });

  // Session visit tracking — records each series page view so a non-series
  // signup page can fall back to the most-visited series this session.
  var STORAGE_KEY = 'lc_sv';

  function recordSeriesVisit(seriesData) {
    try {
      var d = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
      d[seriesData.name] = (d[seriesData.name] || 0) + 1;
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(d));
    } catch (_e) {}
  }

  function topFromSessionHistory() {
    try {
      var d = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
      var best = null, top = 0;
      Object.keys(d).forEach(function (name) {
        if (d[name] > top && LC_SERIES_BY_NAME[name]) {
          best = LC_SERIES_BY_NAME[name];
          top = d[name];
        }
      });
      return best;
    } catch (_e) { return null; }
  }

  function getSeriesFromPage() {
    // Product page: series name is the first element of the Categories array.
    if (window.lcKlaviyoProduct) {
      var cats = window.lcKlaviyoProduct.Categories || [];
      for (var i = 0; i < cats.length; i++) {
        if (LC_SERIES_BY_NAME[cats[i]]) return LC_SERIES_BY_NAME[cats[i]];
      }
    }
    // Collection page: series handle is in the URL path.
    var m = window.location.pathname.match(/\/collections\/([a-z0-9-]+)/);
    if (m && LC_SERIES[m[1]]) return LC_SERIES[m[1]];
    return null;
  }

  // Record this page's series on load so form-submit fallback has history.
  var currentSeries = getSeriesFromPage();
  if (currentSeries) recordSeriesVisit(currentSeries);

  document.addEventListener('klaviyoForms', function (e) {
    var t = e.detail && e.detail.type;
    if (t !== 'submit' && t !== 'formSubmitSuccess') return;
    // Primary: series page. Fallback: most-visited series this session.
    var s = getSeriesFromPage() || topFromSessionHistory();
    if (s) {
      var lq = (window._learnq = window._learnq || []);
      lq.push(['identify', {
        featured_series_name: s.name,
        featured_series_handle: LC_SERIES_HANDLE[s.name] || '',
        featured_series_confidence: 'browse-context',
        featured_series_quote_lead: s.ql,
        featured_series_quote_body: s.qb
      }]);
    }
    // GA4 generate_lead on confirmed hosted-form success (LOS7-861).
    if (t === 'formSubmitSuccess' && typeof window.lcTrackSignup === 'function') {
      window.lcTrackSignup('Klaviyo Form');
    }
  });
}());
